/**
 * Request queue utility to limit concurrent API requests
 * Prevents overwhelming the server with too many parallel requests
 */

type QueuedRequest<T> = {
	execute: () => Promise<T>
	reject: (error: unknown) => void
	resolve: (value: T) => void
}

export type RequestQueueInstance = {
	enqueue: <T>(request: () => Promise<T>) => Promise<T>
	getPendingCount: () => number
	getRunningCount: () => number
	setMaxConcurrent: (max: number) => void
}

export const createRequestQueue = (
	initialMaxConcurrent = 4,
): RequestQueueInstance => {
	const queue: QueuedRequest<unknown>[] = []
	let activeCount = 0
	let maxConcurrent = initialMaxConcurrent

	const processQueue = async (): Promise<void> => {
		if (activeCount >= maxConcurrent || queue.length === 0) {
			return
		}

		const request = queue.shift()
		if (!request) return

		activeCount++

		try {
			const result = await request.execute()
			request.resolve(result)
		} catch (error) {
			request.reject(error)
		} finally {
			activeCount--
			processQueue()
		}
	}

	const enqueue = <T>(request: () => Promise<T>): Promise<T> => {
		return new Promise<T>((resolve, reject) => {
			queue.push({
				execute: request as () => Promise<unknown>,
				resolve: resolve as (value: unknown) => void,
				reject,
			})
			processQueue()
		})
	}

	const getPendingCount = () => queue.length

	const getRunningCount = () => activeCount

	const setMaxConcurrent = (max: number) => {
		maxConcurrent = max
		processQueue()
	}

	return {
		enqueue,
		getPendingCount,
		getRunningCount,
		setMaxConcurrent,
	}
}

// Singleton instance with default of 10 concurrent requests
export const requestQueue = createRequestQueue(10)
